import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { selectIsSystemSession } from 'features/application/selectors';
import { selectPinPanelsEnabled } from 'features/room/roomSlice';
import { selectHasPermissions } from 'features/permissions/permissionsSlice';
import { PermissionTypes } from 'features/permissions/types';
import {
  BroadcastMessageFormType,
  DEFAULT_SPLIT_RATIO,
  MAX_PINNED_PANELS,
  SidePanelData,
  SidePanelPlacement,
  SidePanelState,
  SidePanelType,
} from 'features/side-panels/types';

export const initialState: SidePanelState = {
  activePanel: null,
  pinnedPanels: [],
  remotePinnedPanels: [],
  pinnedSplitRatio: DEFAULT_SPLIT_RATIO,
  data: {},
  placement: 'left',
  broadcastMessageForm: null,
};

export const sidePanelSlice = createSlice({
  name: 'sidePanels',
  initialState,
  reducers: {
    sidePanelOpened: {
      reducer: (state, action: PayloadAction<SidePanelType, string, SidePanelData>) => {
        if (state.pinnedPanels.includes(action.payload)) {
          return;
        }

        state.activePanel = action.payload;
        state.data = action.meta;
      },
      prepare: (payload: SidePanelType, meta: SidePanelData = {}) => ({
        payload,
        meta,
      }),
    },
    sidePanelClosed(state, action: PayloadAction<SidePanelType>) {
      const type = action.payload;

      if (state.pinnedPanels.includes(type)) {
        const prevLength = state.pinnedPanels.length;
        state.pinnedPanels = state.pinnedPanels.filter((p) => p !== type);

        if (state.pinnedPanels.length !== prevLength) {
          state.pinnedSplitRatio = DEFAULT_SPLIT_RATIO;
        }
        return;
      }

      if (state.activePanel === type) {
        state.activePanel = null;
      }
    },
    sidePanelPinned(state, action: PayloadAction<SidePanelType>) {
      const type = action.payload;

      if (state.pinnedPanels.includes(type)) {
        return;
      }

      if (state.pinnedPanels.length >= MAX_PINNED_PANELS) {
        state.pinnedPanels.shift();
      }

      state.pinnedPanels.push(type);
      state.pinnedSplitRatio = DEFAULT_SPLIT_RATIO;

      if (state.activePanel === type) {
        state.activePanel = null;
      }
    },
    sidePanelUnpinned(state, action: PayloadAction<SidePanelType>) {
      const type = action.payload;

      if (!state.pinnedPanels.includes(type)) {
        return;
      }

      const wasSinglePinned = state.pinnedPanels.length === 1;
      const hasOverlay = state.activePanel !== null;

      state.pinnedPanels = state.pinnedPanels.filter((p) => p !== type);
      state.pinnedSplitRatio = DEFAULT_SPLIT_RATIO;

      if (wasSinglePinned && !hasOverlay) {
        state.activePanel = type;
      }
    },
    pinnedSplitRatioChanged(state, action: PayloadAction<number>) {
      state.pinnedSplitRatio = action.payload;
    },
    sidePanelDataCleared(state) {
      state.data = {};
    },
    sidePanelPlacementChanged(state, action: PayloadAction<SidePanelPlacement>) {
      state.placement = action.payload;
    },
    broadcastMessageFormOpened: (state, action: PayloadAction<BroadcastMessageFormType>) => {
      state.broadcastMessageForm = action.payload;
    },
    broadcastMessageFormClosed: (state) => {
      state.broadcastMessageForm = null;
    },
    remotePinnedPanelsReceived(state, action: PayloadAction<{ panels: SidePanelType[] }>) {
      state.remotePinnedPanels = action.payload.panels;
    },
    pinnedPanelsReceived(state, action: PayloadAction<{ panels: SidePanelType[] }>) {
      const prevLength = state.pinnedPanels.length;
      state.pinnedPanels = action.payload.panels;

      if (state.pinnedPanels.length !== prevLength) {
        state.pinnedSplitRatio = DEFAULT_SPLIT_RATIO;
      }

      if (state.activePanel && state.pinnedPanels.includes(state.activePanel)) {
        state.activePanel = null;
      }
    },
  },
});

export const signalingPinnedPanelsChanged = createAction<{ panels: SidePanelType[] }>(
  'signaling/pinnedPanelsChanged'
);

export const {
  sidePanelOpened,
  sidePanelClosed,
  sidePanelPinned,
  sidePanelUnpinned,
  pinnedSplitRatioChanged,
  sidePanelDataCleared,
  sidePanelPlacementChanged,
  broadcastMessageFormOpened,
  broadcastMessageFormClosed,
  pinnedPanelsReceived,
  remotePinnedPanelsReceived,
} = sidePanelSlice.actions;

export const selectSidePanel = (state: RootState) => state.sidePanels.general.activePanel;

export const selectSidePanelData = (state: RootState) => state.sidePanels.general.data;

export const selectSidePanelPlacement = (state: RootState) => state.sidePanels.general.placement;

export const selectPinnedPanels = (state: RootState) => state.sidePanels.general.pinnedPanels;

export const selectPinnedSplitRatio = (state: RootState) =>
  state.sidePanels.general.pinnedSplitRatio;

export const selectIsSidePanelOpen = (state: RootState) =>
  state.sidePanels.general.pinnedPanels.length > 0 || state.sidePanels.general.activePanel !== null;

export const selectBroadcastMessageForm = (state: RootState) =>
  state.sidePanels.general.broadcastMessageForm;

export const selectRemotePinnedPanels = (state: RootState) =>
  state.sidePanels.general.remotePinnedPanels;

export const selectPinnedPanelsAvailable = (state: RootState) =>
  selectPinPanelsEnabled(state) &&
  !selectIsSystemSession(state) &&
  selectHasPermissions(state, PermissionTypes.pinPanels);

export default sidePanelSlice.reducer;
